/**
 * Artifacts, grouped and searched.
 *
 * WHY. The shelf is a flat list, which is the right shape for twenty things
 * and the wrong one for two hundred. Every artifact already knows the subject it
 * came from, so that is the axis: a subject is the unit of work people think
 * in, and "what has Trucking produced" is the question a flat list answers
 * worst.
 *
 * Dependency-free so it can be tested — the grouping is where this screen can
 * quietly be wrong, and the failure is invisible: a mis-sorted or
 * silently-dropped group looks exactly like a shelf with less on it.
 */
import type { ArtifactSummary } from '../types';

export type GroupBy = 'none' | 'subject' | 'kind';

export interface ArtifactGroup {
  key: string;
  label: string;
  items: ArtifactSummary[];
}

const UNFILED = 'Not in a subject';

/** Case-insensitive substring over the title and the subject name. */
export function searchArtifacts(items: ArtifactSummary[], query: string): ArtifactSummary[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(
    (a) =>
      (a.title ?? '').toLowerCase().includes(q) ||
      (a.project_name ?? '').toLowerCase().includes(q),
  );
}

export function groupArtifacts(items: ArtifactSummary[], by: GroupBy): ArtifactGroup[] {
  if (by === 'none') return [{ key: 'all', label: '', items }];

  const buckets = new Map<string, ArtifactGroup>();
  for (const a of items) {
    const label =
      by === 'subject' ? (a.project_name ?? UNFILED) : String(a.kind ?? 'other');
    const key = `${by}:${label}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.items.push(a);
    else buckets.set(key, { key, label, items: [a] });
  }

  return Array.from(buckets.values()).sort((x, y) => {
    // Unfiled last, always. It is a residue rather than a subject, and sorting
    // it alphabetically buries real groups underneath it.
    if (x.label === UNFILED) return 1;
    if (y.label === UNFILED) return -1;
    return x.label.localeCompare(y.label);
  });
}
